class CartNotification extends HTMLElement {
  constructor() {
    super();

    this.notification = document.getElementById('cart-notification');
    this.header = document.querySelector('sticky-header');
    this.onBodyClick = this.handleBodyClick.bind(this);

    this.shippingThreshold = 7500;
    this.multiBuyCollectionUrl = this.dataset.multibuyCollectionUrl || '';
    this.eligibleProductIdsPromise = null;

    this.notification.addEventListener(
      'keyup',
      (evt) => evt.code === 'Escape' && this.close()
    );

    this.querySelectorAll('button[type="button"]').forEach((closeButton) =>
      closeButton.addEventListener(
        'click',
        this.close.bind(this)
      )
    );
  }

  open() {
    this.notification.classList.add(
      'animate',
      'active'
    );

    this.updateCartPromotions();

    this.notification.addEventListener(
      'transitionend',
      () => {
        this.notification.focus();
        trapFocus(this.notification);
      },
      { once: true }
    );

    document.body.addEventListener(
      'click',
      this.onBodyClick
    );

    this.dispatchCartViewEvent();
  }

  async updateCartPromotions() {
    try {
      const response = await fetch(
        `${routes.cart_url}.js`,
        {
          credentials: 'same-origin',
        }
      );

      if (!response.ok) return;

      const cart = await response.json();

      this.updateShippingProgressFromCart(cart);
      await this.updateMultiBuyProgressFromCart(cart);

    } catch (error) {
      console.log(
        'Cart promotion tracker update skipped.'
      );
    }
  }

  updateShippingProgressFromCart(cart) {
    const message = this.querySelector(
      '#osds-notification-shipping-message'
    );

    const fill = this.querySelector(
      '#osds-notification-shipping-fill'
    );

    const track = this.querySelector(
      '#osds-notification-shipping-track'
    );

    if (!message || !fill || !track) {
      return;
    }

    const total =
      cart.total_price || 0;

    let progress =
      (total / this.shippingThreshold) * 100;

    progress = Math.max(
      0,
      Math.min(100, progress)
    );

    fill.style.width =
      `${progress}%`;

    track.setAttribute(
      'aria-valuenow',
      Math.round(progress)
    );

    if (
      total >=
      this.shippingThreshold
    ) {
      message.textContent =
        "🎆 You've unlocked free U.S. shipping! 🎆";
    } else {
      const remaining =
        (
          this.shippingThreshold -
          total
        ) / 100;

      message.textContent =
        `You're $${remaining.toFixed(2)} away from free U.S. shipping`;
    }
  }

  async updateMultiBuyProgressFromCart(cart) {
    const container = this.querySelector(
      '#osds-notification-multibuy'
    );

    const message = this.querySelector(
      '#osds-notification-multibuy-message'
    );

    const countLabel = this.querySelector(
      '#osds-notification-multibuy-count'
    );

    if (!container || !message || !countLabel) {
      return;
    }

    if (!this.multiBuyCollectionUrl) {
      container.hidden = true;
      return;
    }

    try {
      const eligibleProductIds =
        await this.getEligibleProductIds();

      let eligibleCount = 0;

      (cart.items || []).forEach((item) => {
        const productId =
          Number(item.product_id);

        const quantity =
          Number(item.quantity || 0);

        if (
          eligibleProductIds.has(productId)
        ) {
          eligibleCount += quantity;
        }
      });

      container.hidden = false;

      countLabel.textContent =
        `${eligibleCount} ${eligibleCount === 1 ? 'die' : 'dice'} 🎲`;

      /*
        Before 10% tier
      */
      if (eligibleCount < 3) {
        const needed =
          3 - eligibleCount;

        message.textContent =
          `Add ${needed} more ${needed === 1 ? 'die' : 'dice'} to unlock 10% off`;

        return;
      }

      /*
        10% tier unlocked
      */
      if (eligibleCount < 6) {
        const needed =
          6 - eligibleCount;

        message.textContent =
          `🎆 10% off unlocked! 🎆 Add ${needed} more ${needed === 1 ? 'die' : 'dice'} for 15% off`;

        return;
      }

      /*
        15% tier unlocked
      */
      message.textContent =
        '🎆 15% off unlocked! 🎆';

    } catch (error) {
      container.hidden = true;

      console.log(
        'Multi-buy tracker update skipped.'
      );
    }
  }

  getEligibleProductIds() {
    if (this.eligibleProductIdsPromise) {
      return this.eligibleProductIdsPromise;
    }

    this.eligibleProductIdsPromise =
      this.fetchEligibleProductIds();

    return this.eligibleProductIdsPromise;
  }

  async fetchEligibleProductIds() {
    const productIds =
      new Set();

    for (
      let page = 1;
      page <= 20;
      page += 1
    ) {
      const separator =
        this.multiBuyCollectionUrl.includes('?')
          ? '&'
          : '?';

      const url =
        `${this.multiBuyCollectionUrl}/products.json` +
        `${separator}limit=250&page=${page}`;

      const response =
        await fetch(
          url,
          {
            credentials: 'same-origin',
          }
        );

      if (!response.ok) {
        throw new Error(
          'Unable to read multi-buy collection.'
        );
      }

      const data =
        await response.json();

      const products =
        Array.isArray(data.products)
          ? data.products
          : [];

      products.forEach((product) => {
        productIds.add(
          Number(product.id)
        );
      });

      if (products.length < 250) {
        break;
      }
    }

    return productIds;
  }

  async dispatchCartViewEvent() {
    const { CartViewEvent } =
      window.StandardEvents || {};

    if (!CartViewEvent) return;

    try {
      const response = await fetch(
        `${routes.cart_url}.json`
      );

      const cart =
        await response.json();

      if (!cart?.currency) return;

      this.dispatchEvent(
        new CartViewEvent({
          context: 'dialog',

          cart:
            CartViewEvent.createCartFromAjaxResponse(
              cart
            ),
        })
      );

    } catch (e) {
      /* informational only */
    }
  }

  close() {
    this.notification.classList.remove(
      'active'
    );

    document.body.removeEventListener(
      'click',
      this.onBodyClick
    );

    removeTrapFocus(
      this.activeElement
    );
  }

  renderContents(parsedState) {
    this.cartItemKey =
      parsedState.key;

    this.getSectionsToRender().forEach(
      (section) => {
        document.getElementById(
          section.id
        ).innerHTML =
          this.getSectionInnerHTML(
            parsedState.sections[
              section.id
            ],
            section.selector
          );
      }
    );

    if (this.header) {
      this.header.reveal();
    }

    this.open();
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-notification-product',

        selector:
          `[id="cart-notification-product-${this.cartItemKey}"]`,
      },

      {
        id: 'cart-notification-button',
      },

      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionInnerHTML(
    html,
    selector = '.shopify-section'
  ) {
    return new DOMParser()
      .parseFromString(
        html,
        'text/html'
      )
      .querySelector(
        selector
      ).innerHTML;
  }

  handleBodyClick(evt) {
    const target =
      evt.target;

    if (
      target !==
        this.notification &&
      !target.closest(
        'cart-notification'
      )
    ) {
      const disclosure =
        target.closest(
          'details-disclosure, header-menu'
        );

      this.activeElement =
        disclosure
          ? disclosure.querySelector(
              'summary'
            )
          : null;

      this.close();
    }
  }

  setActiveElement(element) {
    this.activeElement =
      element;
  }
}

customElements.define(
  'cart-notification',
  CartNotification
);